import React, { useState } from "react";
import { auth, db } from "./firebase";
import { signInWithEmailAndPassword, setPersistence, browserLocalPersistence } from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import { Shield, ArrowRight, Mail, Key, Activity, Globe } from "lucide-react";

// Image import
import loginPic from "./loginpic.jpg"; 

const HospitalLoginPage = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  // Simplified Login: No OTP, No Backend API
  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      // 1. Set persistence and sign in
      await setPersistence(auth, browserLocalPersistence);
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      // 2. Fetch hospital data to get the name for the URL
      const hospitalRef = doc(db, "hospitals", user.uid);
      const hospitalSnap = await getDoc(hospitalRef);

      if (hospitalSnap.exists()) {
        const hospitalData = hospitalSnap.data();
        const formattedName = hospitalData.hospitalName
          .toLowerCase()
          .trim()
          .replace(/\s+/g, '-');
        
        // Redirect to dynamic dashboard
        navigate(`/${formattedName}/dashboard`);
      } else {
        // Fallback if data not found
        navigate("/dashboard");
      }
    } catch (error) {
      alert(error.message || "Invalid Email or Password ❌");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="h-screen w-full bg-white flex overflow-hidden font-sans">
      
      {/* LEFT SIDE: Image Section (Register Page Style Overlay) */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden bg-[#090B0E]">
        <img 
          src={loginPic} 
          alt="Medical Infrastructure" 
          className="absolute inset-0 w-full h-full object-cover opacity-20 scale-105"
        />
        
        <div className="absolute inset-0 w-full h-full object-cover opacity-40 scale-105"></div>
        
        <div className="absolute top-12 left-12 z-20">
          <div className="flex items-center gap-2 bg-white/5 backdrop-blur-sm px-4 py-2 rounded-full border border-white/10">
            <Globe size={14} className="text-slate-400" />
            <span className="text-[10px] text-slate-300 font-bold uppercase tracking-[0.2em]">Enterprise System</span>
          </div>
        </div>

        <div className="absolute bottom-16 left-16 z-20 space-y-4 max-w-md animate-in slide-in-from-bottom-8 duration-700">
          <div className="h-1 w-12 bg-blue-600 rounded-full"></div>
          <h2 className="text-4xl font-black text-white mb-4 leading-tight">
              Welcome Back to <br />
              <span className="text-blue-500">Avaamiop.</span>
          </h2>
          <p className="text-slate-400 text-sm font-medium tracking-wide">
            Access your secure portal to manage patients, <br /> 
            records, and facility logistics.
          </p>
        </div>
      </div>

      {/* RIGHT SIDE: Form Section */}
      <div className="w-full lg:w-1/2 bg-white flex flex-col justify-between relative shadow-2xl">
        
        {/* Logo Section */}
        <div className="p-8 flex justify-between items-center">
            <div className="flex items-center gap-2">
               <div className="h-8 w-8 bg-blue-600 rounded-lg flex items-center justify-center">
                  <Activity className="text-white" size={18} />
               </div>
               <span className="font-black text-xl tracking-tighter text-slate-900">Avaami<span className="text-blue-600">op</span></span>
            </div>
            <button className="text-[10px] font-black text-slate-400 uppercase tracking-widest hover:text-blue-600 transition-colors">
              Support
            </button>
        </div>

        <div className="flex-1 flex items-center justify-center p-8">
          <div className="w-full max-w-[360px] space-y-8">
            <div className="space-y-2 text-center">
              <h1 className="text-3xl font-black text-slate-900 tracking-tight">Admin Login</h1>
              <div className="h-1 w-8 bg-blue-600 rounded-full mx-auto"></div>
              <p className="text-slate-400 text-sm font-medium pt-2">
                Please enter your credentials to access the console.
              </p>
            </div>

            <div className="space-y-6 animate-in fade-in slide-in-from-right-4 duration-500">
              <form onSubmit={handleLogin} className="space-y-4">
                <div className="space-y-3">
                  <div className="relative group">
                    <input 
                      type="email" 
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="admin@hospital.com" 
                      className="w-full px-5 py-4 bg-slate-50 border-2 border-transparent rounded-xl focus:border-blue-600/10 focus:bg-white transition-all outline-none text-slate-800 font-semibold" 
                      required 
                    />
                    <Mail className="absolute right-5 top-1/2 -translate-y-1/2 text-slate-300 group-focus-within:text-blue-600 transition-colors" size={18} />
                  </div>

                  <div className="relative group">
                    <input 
                      type="password" 
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Password" 
                      className="w-full px-5 py-4 bg-slate-50 border-2 border-transparent rounded-xl focus:border-blue-600/10 focus:bg-white transition-all outline-none text-slate-800 font-semibold" 
                      required 
                    />
                    <Key className="absolute right-5 top-1/2 -translate-y-1/2 text-slate-300 group-focus-within:text-blue-600 transition-colors" size={18} />
                  </div>
                </div>

                <button 
                  type="submit" 
                  disabled={loading}
                  className="w-full bg-[#0A0C10] text-white font-black py-4 rounded-xl shadow-xl hover:bg-blue-600 active:scale-[0.98] transition-all flex items-center justify-center gap-2 uppercase tracking-widest text-[11px]"
                >
                  {loading ? "Verifying..." : "Sign In"}
                  {!loading && <ArrowRight size={14} />}
                </button>
              </form>

              <div className="relative flex items-center py-2">
                <div className="flex-grow border-t border-slate-100"></div>
                <span className="flex-shrink mx-4 text-[10px] font-bold text-slate-300 uppercase tracking-widest">Help</span>
                <div className="flex-grow border-t border-slate-100"></div>
              </div>

              <button type="button" className="w-full border-2 border-slate-100 text-slate-500 font-bold py-4 rounded-xl hover:bg-slate-50 transition-all flex items-center justify-center gap-3 text-[11px] uppercase tracking-widest">
                <img src="https://www.google.com/favicon.ico" alt="google" className="w-4 h-4 grayscale opacity-70" />
                Google Auth
              </button>
            </div>
          </div>
        </div>
        
        <div className="p-8 flex justify-between items-center text-[10px] font-black text-slate-300 uppercase tracking-widest border-t border-slate-50">
            <p>© 2026 Avaamiop</p>
            <button 
              onClick={() => navigate('/register')}
              className="text-blue-600 hover:underline underline-offset-4"
            >
              Create New Account
            </button>
        </div>
      </div>
    </div>
  );
};

export default HospitalLoginPage;